import React, {useRef, useState} from 'react';
import {Plus, X, Trash} from 'lucide-react';
import {cn} from '../../../lib/utils';

interface MultipleFileSelectProps {
	name: string;
	placeholder: string;
	onChange: (files: File[]) => void;
	error?: string;
	defaultValue?: File[];
	className?: string;
	inputClassName?: string;
}

export const MultipleFileSelect: React.FC<MultipleFileSelectProps> = ({
	name,
	placeholder,
	className,
	inputClassName,
	onChange,
	error,
	defaultValue = []
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [files, setFiles] = useState<File[]>(defaultValue);

	const handleAddClick = () => inputRef.current?.click();

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
		const updatedFiles = [
			...files,
			...selectedFiles
		];
		setFiles(updatedFiles);
		onChange(updatedFiles);
	};

	const removeFile = (index: number) => {
		const updated = files.filter((_, i) => i !== index);
		setFiles(updated);
		onChange(updated);
	};

	const clearAll = () => {
		setFiles([]);
		onChange([]);
		if (inputRef.current) {
			inputRef.current.value = '';
		}
	};

	const downloadFile = (file: File) => {
		const url = URL.createObjectURL(file);
		const link = document.createElement('a');
		link.href = url;
		link.download = file.name;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	return (
		<div className={cn(`flex flex-col`, className)}>
			<div
				className={cn(`relative flex peer w-full h-full bg-white min-h-[56px] placeholder:text-transparent text-blue-dark text-sm border rounded-[6px] px-3 py-2 transition duration-300 ease focus:outline-none
								${error ? 'border-red-600' : 'border-gray-300'}
								${files.length > 0 ? 'items-end  justify-between' : 'items-center justify-end'}`, inputClassName)}>
				<label className={`absolute bg-white rounded-l px-1 left-2.5 transition-all transform origin-left
						${error ? 'text-error ' : 'text-gray-500 '}
						${files.length > 0 ? '-top-2 text-sm' : 'top-[16px] text-[16px]'}
					`}>{placeholder}
				</label>
				<input
					type="file"
					multiple
					hidden
					ref={inputRef}
					onChange={handleChange}
					name={name}
				/>
				{files.length > 0 && (
					<ul className="flex items-center flex-wrap gap-2 h-full">
						{files.map((file, i) => (
							<li key={i} className="flex items-center justify-between gap-2 bg-gray-300 px-2 rounded-full hover:opacity-80 cursor-pointer">
								<span onClick={() => downloadFile(file)} className="">{file.name}</span>
								<button type="button" onClick={() => removeFile(i)}>
									<X size={14}
									   className="text-white bg-grey-dark rounded-full"/>
								</button>
							</li>
						))}
					</ul>
				)}
				<div className="flex items-center gap-2">
					<div
						onClick={handleAddClick}
						className="flex items-center justify-center w-12 h-8  hover:bg-gray-200 rounded border border-blue-dark cursor-pointer"
					>
						<Plus size={22}/>
					</div>
					{files.length > 0 &&
						<div
							onClick={clearAll}
							className="flex items-center justify-center w-12 h-8  hover:bg-gray-200 rounded border border-blue-dark cursor-pointer"
						>
							<Trash size={22}/>
						</div>
					}
				</div>
			</div>
			{error && (
				<p className="mt-[6px] px-[16px] text-[12px] text-error leading-3">{error}</p>
			)}
		</div>
	);
};
